import LogoSmall from "@assets/CCAU_Logo_Small.svg?react";
import { cn, useMediaScreen } from "@cca/components";
import { useSettings } from "@contexts";
import { useOutsideClickHandler } from "@core/hooks";
import { useEffect, useRef } from "react";
import { Location, NavLink, useLocation } from "react-router-dom";

import ToggleSidebarButton, {
  ToggleSidebarButtonProps,
} from "./SidebarCollapseButton";
import { SidebarNavigationItems } from "./SidebarNavigationItems";
import SidebarUserInfo from "./SidebarUserInfo";

type SidebarProps = ToggleSidebarButtonProps & {
  collapsed: boolean;
  onClose: () => void;
};

const logoClassName = "absolute h-full w-auto duration-300 pl-0.5";

const Sidebar = ({ onClose, onToggle, collapsed }: SidebarProps) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const memoizedLocation = useRef<Location | null>(null);
  const { AppLogo } = useSettings();

  const location = useLocation();

  const lgScreen = useMediaScreen("lg");

  const onUserInfoClick = () => {
    if (collapsed && lgScreen) onToggle();
  };

  useEffect(() => {
    const prev = memoizedLocation.current;

    // Always update the stored location
    memoizedLocation.current = location;

    // Skip if it's the very first run (prev is null)
    if (!prev) return;

    // Close only on real route change
    if (!collapsed && !lgScreen && prev.pathname !== location.pathname) {
      onClose();
    }
  }, [location, collapsed, lgScreen, onClose]);

  useOutsideClickHandler(sidebarRef, () => {
    if (!lgScreen && !collapsed) onClose();
  });

  return (
    <div
      className={cn(
        "fixed right-full z-50 grid max-h-screen min-h-screen grid-cols-[13rem_auto] border-r border-gray-100 bg-white shadow-xl shadow-gray-300/5 transition-all duration-300 ease-in-out lg:sticky lg:top-0 lg:right-auto lg:translate-x-0",
        {
          "translate-x-full": !collapsed,
          "lg:grid-cols-[4.5rem_auto]": collapsed,
        },
      )}
      ref={sidebarRef}
    >
      <ToggleSidebarButton collapsed={collapsed} onToggle={onToggle} />
      <div className="flex h-full max-h-screen flex-col gap-4 overflow-x-hidden overflow-y-auto">
        <nav className="flex flex-1 flex-col gap-7 px-4 pt-9">
          <NavLink aria-label="Home page" to="/">
            <div className="relative flex h-8 w-full items-start">
              <LogoSmall className={logoClassName} data-testid="ccau-logo" />
              <AppLogo
                className={cn(logoClassName, { "opacity-0": collapsed })}
              />
            </div>
          </NavLink>
          <SidebarNavigationItems collapsed={collapsed} />
        </nav>
        <SidebarUserInfo
          collapsed={!!collapsed}
          onAvatarClick={onUserInfoClick}
        />
      </div>
    </div>
  );
};

export default Sidebar;
